'use client';

import type { BlogPost } from '@/content/blog/types';
import AppImage from '@/components/ui/AppImage';

interface BlogGridProps {
  posts: BlogPost[];
  currentLanguage: 'en' | 'de';
  onCardClick: (slug: string, event: React.MouseEvent) => void;
}

export default function BlogGrid({ posts, currentLanguage, onCardClick }: BlogGridProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const translations = {
    en: {
      readMore: 'Read Article',
      by: 'by',
    },
    de: {
      readMore: 'Artikel lesen',
      by: 'von',
    },
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
      {posts.map((post, index) => (
        <article
          key={post.slug}
          className="group relative bg-secondary rounded-xl overflow-hidden shadow-card hover:shadow-cosmic transition-all duration-slow transform hover:-translate-y-2 animate-fade-in"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          {/* Cover Image */}
          <div className="relative h-64 overflow-hidden">
            <AppImage
              src={post.coverImage}
              alt={currentLanguage === 'de' && post.titleDe ? post.titleDe : post.title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              loading="lazy"
              className="object-cover transition-transform duration-slow group-hover:scale-110"
            />

            {/* Tags Badge */}
            {post.tags && post.tags.length > 0 && (
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 bg-accent/90 backdrop-blur-sm text-primary rounded-full text-xs font-semibold uppercase tracking-wider">
                  {currentLanguage === 'de' && post.tagsDe ? post.tagsDe[0] : post.tags[0]}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-foreground-muted">
              <span>{formatDate(post.date)}</span>
              {post.readingTime && (
                <span>{currentLanguage === 'de' && post.readingTimeDe ? post.readingTimeDe : post.readingTime}</span>
              )}
            </div>

            {/* Title */}
            <h2 className="font-heading font-bold text-xl text-foreground-light group-hover:text-accent transition-colors duration-fast line-clamp-2">
              {currentLanguage === 'de' && post.titleDe ? post.titleDe : post.title}
            </h2>

            {/* Author */}
            <p className="text-sm text-foreground-secondary">
              {translations[currentLanguage].by} {post.author}
            </p>

            {/* Summary */}
            <p className="text-foreground-muted font-inter text-sm line-clamp-3">
              {currentLanguage === 'de' && post.summaryDe ? post.summaryDe : post.summary}
            </p>

            {/* Tags */}
            {post.tags && post.tags.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {(currentLanguage === 'de' && post.tagsDe ? post.tagsDe : post.tags).slice(1, 4).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-highlight/10 text-highlight rounded text-xs font-inter font-medium"
                  >
                    {tag}
                  </span>
                ))}
                {(currentLanguage === 'de' && post.tagsDe ? post.tagsDe.length : post.tags.length) > 4 && (
                  <span className="px-2 py-1 bg-highlight/10 text-highlight rounded text-xs font-inter font-medium">
                    +{(currentLanguage === 'de' && post.tagsDe ? post.tagsDe.length : post.tags.length) - 4}
                  </span>
                )}
              </div>
            )}

            {/* CTA Button */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onCardClick(post.slug, e);
              }}
              className="w-full mt-4 px-6 py-3 bg-primary hover:bg-accent/10 border-2 border-accent text-accent rounded-md font-inter font-semibold text-sm transition-all duration-fast hover:shadow-glow-cyan group/btn cursor-pointer"
            >
              <span className="flex items-center justify-center">
                {translations[currentLanguage].readMore}
                <svg
                  className="w-4 h-4 ml-2 transition-transform duration-fast group-hover/btn:translate-x-1"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </button>
          </div>

          {/* Hover Effect Overlay */}
          <div className="absolute inset-0 border-2 border-accent opacity-0 group-hover:opacity-100 transition-opacity duration-fast rounded-xl pointer-events-none" />
        </article>
      ))}
    </div>
  );
}
